import type { ApiClient } from './client';
import type {
  AdCreative,
  AuthenticatedHomeData,
  FeaturedBuild,
  PublicHomeData,
} from '../../components/home/homeTypes';
import type { HomeFeedResponse, HomeFeedStateAction } from '../../components/home/homeTypes';

export const homeMethods = {
  async getHomePublic(): Promise<PublicHomeData> {
    const response = await this.makeRequest<PublicHomeData>('/home_public.php');
    if (!response.success) {
      throw new Error(response.message || 'Failed to load homepage');
    }
    return response.data || {};
  },

  async getHomeAuthenticated(): Promise<AuthenticatedHomeData> {
    const response = await this.makeRequest<AuthenticatedHomeData>('/home_authenticated.php', {
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to load homepage');
    }
    return response.data || {};
  },

  async getHomeFeed(params: {
    cursor?: string | number;
    limit?: number;
    types?: string;
    include_read?: boolean;
  } = {}): Promise<HomeFeedResponse> {
    const qs = new URLSearchParams();
    if (params.cursor != null) qs.set('cursor', String(params.cursor));
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.types) qs.set('types', params.types);
    if (params.include_read) qs.set('include_read', '1');
    const suffix = qs.toString() ? `?${qs}` : '';
    const response = await this.makeRequest<HomeFeedResponse>(`/home_feed.php${suffix}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to load home feed');
    }
    return (
      response.data || {
        generated_at: new Date().toISOString(),
        summary: {
          greeting: 'Welcome back',
          date_label: '',
          headline: "You're caught up.",
          counts: { action_required: 0, events: 0, passport: 0, builds: 0 },
        },
        quick_actions: [],
        sections: [],
      }
    );
  },

  async setHomeFeedState(action: HomeFeedStateAction, itemKey: string): Promise<void> {
    const response = await this.makeRequest('/home_feed.php', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action, item_key: itemKey }),
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update feed item');
    }
  },

  async getFeaturedBuilds(): Promise<FeaturedBuild | FeaturedBuild[] | null> {
    const response = await this.makeRequest<FeaturedBuild | FeaturedBuild[] | { featured_build?: FeaturedBuild }>(
      '/featured_builds.php'
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to load featured builds');
    }
    const data = response.data;
    if (!data) return null;
    if (Array.isArray(data)) return data;
    if ('featured_build' in data) return data.featured_build || null;
    return data as FeaturedBuild;
  },

  async getAdPlacement(placement: string): Promise<AdCreative | null> {
    try {
      const response = await this.makeRequest<AdCreative | null>(
        `/ad_placement.php?placement=${encodeURIComponent(placement)}`
      );
      if (!response.success) return null;
      return response.data || null;
    } catch {
      return null;
    }
  },

  async recordAdImpression(payload: {
    creative_id: number;
    campaign_id: number;
    placement: string;
  }): Promise<void> {
    try {
      await this.makeRequest('/ad_impression.php', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.debug('ad impression failed:', error);
    }
  },

  buildAdClickUrl(creative: Pick<AdCreative, 'creative_id' | 'campaign_id' | 'placement'>): string {
    const params = new URLSearchParams({
      creative_id: String(creative.creative_id),
      campaign_id: String(creative.campaign_id),
      placement: creative.placement,
    });
    return `${this.baseUrl}/ad_click.php?${params.toString()}`;
  },
} satisfies ThisType<ApiClient>;
